import React, { useState, useEffect, useCallback } from "react";
import {
  Breadcrumb,
  Container,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { getApprovedHotels, getApprovalStatus, getRejectedHotels, updateHotel } from "../../helpers/api.ts";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const Hotels = () => {
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [hotelType, setHotelType] = useState('active'); 
  const [searchQuery, setSearchQuery] = useState('');
  const [editFormData, setEditFormData] = useState({
    hotel_name: '',
    total_rooms: '',
    address: '',
    star_rating: '',
    hotel_image: null
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0
  });

  const navigate = useNavigate();
const logUser =JSON.parse(localStorage.getItem('user'));
  const toggle = () => setModal(!modal);
  const toggleEditModal = () => setEditModal(!editModal);

  const fetchHotels = async (searchTerm = '') => {
    const response = hotelType === 'active' 
      ? await getApprovedHotels(pagination.currentPage, pagination.pageSize, searchTerm)
      : await getRejectedHotels(pagination.currentPage, pagination.pageSize, searchTerm);
    setHotels(response.data);
    setPagination(response.pagination);
  };
  
  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (searchTerm) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchHotels(searchTerm);
        }, 500);
      };
    })(),
    [hotelType, pagination.currentPage, pagination.pageSize]
  );

  useEffect(() => {
    fetchHotels(searchQuery);
  }, [pagination.currentPage, hotelType]);

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);


  const handleViewDetails = (hotel) => {
    setSelectedHotel(hotel);
    toggle();
  };
  const handleHotelDetails = (id) => {
    if (logUser.role === 'ADMIN'){
    navigate(`/hotels-details/${id}`)
  }else {
    return null;
  }

  };

  const handleEditHotel = (hotel) => {
    setSelectedHotel(hotel);
    setEditFormData({
      hotel_name: hotel.hotel_name || '',
      total_rooms: hotel.total_rooms || '',
      address: hotel.address || '',
      star_rating: hotel.star_rating || '',
      hotel_image: null
    });
    setSelectedFile(null);
    toggleEditModal();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleUpdateHotel = async () => {
    try {
      if (!selectedHotel?.hotel_id) {
        Swal.fire({
          title: "Error!",
          text: "Hotel ID is missing.",
          icon: "error",
          confirmButtonColor: "#dc3545",
        });
        return;
      }

      const formData = new FormData();
      formData.append('hotel_name', editFormData.hotel_name);
      formData.append('total_rooms', editFormData.total_rooms);
      formData.append('address', editFormData.address);
      formData.append('star_rating', editFormData.star_rating);
      
      if (selectedFile) {
        formData.append('hotel_image', selectedFile);
      }

      const response = await updateHotel({
        formData,
        id: selectedHotel.hotel_id
      });

      if (response.message === "Hotel updated successfully") {
        Swal.fire({
          title: "Success!",
          text: "Hotel updated successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        });
        
        toggleEditModal();
        fetchHotels(); // Refresh the hotels list
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to update hotel. Please try again.",
          icon: "error",
          confirmButtonColor: "#dc3545",
        });
      }
    } catch (error) {
      console.error("Error updating hotel:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to update hotel. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
      });
    }
  };
    
    const handleStatusChange = async (hotelId, status) => {
      try {
        Swal.fire({
          title: `Are you sure you want to ${status.toLowerCase()} this hotel?`,
          text: "This action cannot be undone!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: status === "Approved" ? "#28a745" : "#dc3545",
          cancelButtonColor: "#6c757d",
          confirmButtonText: `Yes, ${status.toLowerCase()} it!`,
        }).then(async (result) => {
          if (result.isConfirmed) {
            const response = await getApprovalStatus(hotelId, status);
            if (response.success) {
              const updatedHotels = await getApprovedHotels();
              setHotels(updatedHotels.data);
              toggle();
              
              Swal.fire({
                title: "Success!",
                text: `Hotel has been ${status.toLowerCase()} successfully.`,
                icon: "success",
                confirmButtonColor: "#28a745",
              });
            } else {
              Swal.fire({
                title: "Error!",
                text: "Something went wrong.",
                icon: "error",
                confirmButtonColor: "#dc3545",
              });
            }
          }
        });
      } catch (error) {
        console.error(`Error ${status} hotel:`, error);
        Swal.fire({
          title: "Error!",
          text: `Failed to ${status.toLowerCase()} hotel. Please try again.`,
          icon: "error",
          confirmButtonColor: "#dc3545",
        });
      }
    };

  return (
    <>
      <div className="page-content">
        <Container fluid={false}>
          <Breadcrumb title="" breadcrumbItem="Approved Hotels" />
        </Container>
        <div className="mb-3">
          <Row className="align-items-center g-2">
            <Col xs={12} md={6}>
              <div className="d-flex gap-2 flex-wrap">
                <Button 
                  color={hotelType === 'active' ? 'primary' : 'secondary'}
                  className="flex-grow-1 flex-md-grow-0"
                  onClick={() => setHotelType('active')}
                >
                  Active Hotels
                </Button>
                <Button 
                  color={hotelType === 'deactivated' ? 'primary' : 'secondary'}
                  className="flex-grow-1 flex-md-grow-0"
                  onClick={() => setHotelType('deactivated')}
                >
                  Deactivated Hotels
                </Button>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <Input
                type="text"
                placeholder="Search hotels by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-control"
              />
            </Col>
          </Row>
        </div>
        <div className="table-responsive">
        <Table>
          <thead>
            <tr>
              <th>#</th>
              <th>Hotel Name</th>
              <th className="d-none d-md-table-cell">Owner Name</th>
              <th> Rooms</th>
              <th className="d-none d-lg-table-cell">Address</th>
              <th className="d-none d-lg-table-cell">Approval Date</th>
              <th className="d-none d-md-table-cell">Days Remaining</th>
              <th className="d-none d-md-table-cell">Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {hotels.map((hotel, index) => (
              <tr key={index}>
                <th scope="row">{index + 1}</th>
                <td
                  style={{ cursor:logUser.role === 'ADMIN'? "pointer": null, color:logUser.role === 'ADMIN'? "blue" : "black" }}
                  onClick={() => handleHotelDetails(hotel.hotel_id)}
                  // onClick={() => navigate(`/hotels-details/${hotel.hotel_id}`)}
                >
                  
                  <div className="d-flex flex-column">
                    <span>{hotel.hotel_name}</span>
                    <small className="text-muted d-md-none">{hotel.owner.name}</small>
                    <small className="text-muted d-md-none">{hotel.status}</small>
                  </div>
                </td>
                <td className="d-none d-md-table-cell">{hotel.owner.name}</td>
                <td>{hotel.total_rooms}</td>
                <td className="d-none d-lg-table-cell">{hotel.address}</td>
                <td className="d-none d-lg-table-cell">
                  {hotel.approval_date 
                    ? new Date(hotel.approval_date).toLocaleDateString()
                    : 'N/A'
                  }
                </td>
                <td className="d-none d-md-table-cell">
                  {hotel.days_remaining !== null && hotel.days_remaining !== undefined ? (
                    <span 
                      style={{ 
                        color: hotel.days_remaining <= 30 ? 'red' : 
                               hotel.days_remaining <= 90 ? 'orange' : 'green',
                        fontWeight: hotel.days_remaining <= 30 ? 'bold' : 'normal'
                      }}
                    >
                      {hotel.days_remaining} days
                    </span>
                  ) : (
                    'N/A'
                  )}
                </td>
                <td className="d-none d-md-table-cell">{hotel.status}</td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => handleViewDetails(hotel)}
                    >
                     <i className="mdi mdi-eye"></i>
                  </Button>
                  <Button
                    color="warning"
                    size="sm"
                    onClick={() => handleEditHotel(hotel)}
                  >
                      <i className="mdi mdi-pencil"></i>
                  </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        </div>

        {/* Details Modal */}
        <Modal isOpen={modal} toggle={toggle} size="lg" className="modal-fullscreen-md-down">
          <ModalHeader toggle={toggle}>Hotel Details</ModalHeader>
          <ModalBody>
            {selectedHotel && (
              <div>
                <h5>Hotel Information</h5>
                <p>
                  <strong>Name:</strong> {selectedHotel.hotel_name}
                </p>
                <p>
                  <strong>Address:</strong> {selectedHotel.address}
                </p>
                <p>
                  <strong>Total Rooms:</strong> {selectedHotel.total_rooms}
                </p>

                <h5 className="mt-4">Owner Information</h5>
                <p>
                  <strong>Name:</strong> {selectedHotel.owner.name}
                </p>
                <p>
                  <strong>Email:</strong> {selectedHotel.owner.email}
                </p>

                <h5 className="mt-4">Status</h5>
                <p>
                  <strong>Current Status:</strong> {selectedHotel.status}
                </p>
                
                <h5 className="mt-4">Approval Information</h5>
                <p>
                  <strong>Approval Date:</strong> {selectedHotel.approval_date 
                    ? new Date(selectedHotel.approval_date).toLocaleDateString()
                    : 'Not approved yet'
                  }
                </p>
                {selectedHotel.days_remaining !== null && selectedHotel.days_remaining !== undefined && (
                  <>
                    <p>
                      <strong>Days Remaining:</strong> 
                      <span 
                        style={{ 
                          color: selectedHotel.days_remaining <= 30 ? 'red' : 
                                 selectedHotel.days_remaining <= 90 ? 'orange' : 'green',
                          fontWeight: selectedHotel.days_remaining <= 30 ? 'bold' : 'normal',
                          marginLeft: '10px'
                        }}
                      >
                        {selectedHotel.days_remaining} days
                      </span>
                    </p>
                    <p>
                      <strong>Expires On:</strong> {selectedHotel.expires_on 
                        ? new Date(selectedHotel.expires_on).toLocaleDateString()
                        : 'N/A'
                      }
                    </p>
                  </>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary"  onClick={() => handleStatusChange(selectedHotel.hotel_id, "Approved")}>
            Activate Hotel</Button>
            <Button color="primary"  onClick={() => handleStatusChange(selectedHotel.hotel_id, "Rejected")}>
            Deactivate Hotel</Button>
            <Button color="secondary" onClick={toggle}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit Hotel Modal */}
        <Modal isOpen={editModal} toggle={toggleEditModal} size="lg" className="modal-fullscreen-md-down">
          <ModalHeader toggle={toggleEditModal}>Edit Hotel</ModalHeader>
          <ModalBody>
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="hotel_name">Hotel Name *</Label>
                    <Input
                      type="text"
                      name="hotel_name"
                      id="hotel_name"
                      value={editFormData.hotel_name}
                      onChange={handleInputChange}
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="total_rooms">Total Rooms *</Label>
                    <Input
                      type="number"
                      name="total_rooms"
                      id="total_rooms"
                      value={editFormData.total_rooms}
                      onChange={handleInputChange}
                      required
                      min="1"
                    />
                  </FormGroup>
                </Col>
              </Row>
              
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label for="address">Address *</Label>
                    <Input
                      type="textarea"
                      name="address"
                      id="address"
                      value={editFormData.address}
                      onChange={handleInputChange}
                      required
                      rows="3"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="star_rating">Star Rating</Label>
                    <Input
                      type="select"
                      name="star_rating"
                      id="star_rating"
                      value={editFormData.star_rating}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Rating</option>
                      <option value="1">1 Star</option>
                      <option value="2">2 Stars</option>
                      <option value="3">3 Stars</option>
                      <option value="4">4 Stars</option>
                      <option value="5">5 Stars</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="hotel_image">Hotel Image</Label>
                    <Input
                      type="file"
                      name="hotel_image"
                      id="hotel_image"
                      onChange={handleFileChange}
                      accept="image/*"
                    />
                    {selectedFile && (
                      <small className="text-muted">
                        Selected: {selectedFile.name}
                      </small>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleUpdateHotel}>
              Update Hotel
            </Button>
            <Button color="secondary" onClick={toggleEditModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <div className="d-flex justify-content-between align-items-center mt-3">
  <div>
    Showing {hotels.length} of {pagination.totalItems} entries
  </div>
  <nav>
    <ul className="pagination">
      <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
        <button 
          className="page-link" 
          onClick={() => setPagination(prev => ({...prev, currentPage: prev.currentPage - 1}))}
        >
          Previous
        </button>
      </li>
      {[...Array(pagination.totalPages)].map((_, idx) => (
        <li 
          key={idx} 
          className={`page-item ${pagination.currentPage === idx + 1 ? 'active' : ''}`}
        >
          <button 
            className="page-link" 
            onClick={() => setPagination(prev => ({...prev, currentPage: idx + 1}))}
          >
            {idx + 1}
          </button>
        </li>
      ))}
      <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
        <button 
          className="page-link" 
          onClick={() => setPagination(prev => ({...prev, currentPage: prev.currentPage + 1}))}
        >
          Next
        </button>
      </li>
    </ul>
  </nav>
</div>
      </div>
    </>
  );
};

export default Hotels;
